import { faCheck, faTimes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BatchSelect } from '@modules/Batches/components/BatchSelect';
import { createHttpClient } from '@adapters/HttpClient';
import { Product } from '@shared/components/ui/Product';
import { useToast } from '@shared/hooks/use-toast';
import { apiClientWH } from '@shared/services/ApiClient';
import { useQuery } from '@tanstack/react-query';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductDimensionsApiAdapter } from '../../adapters/ProductDimensionsApiAdapter';
import { PendingProduct } from '../../models/Receive';
import { ReceiveCreateBatchModal, ReceiveCreateBatchModalRef } from './ReceiveCreateBatchModal';

interface QuantityConfirmProps {
  pendingProduct: PendingProduct;
  cartCode?: string;
  onAccept: (
    productId: number,
    locationCode: string,
    qty: number,
    batchId?: number,
  ) => Promise<{ success: boolean; message: string }>;
  onReject: (productId: number, qty: number) => void;
  onCancel: () => void;
}

export const QuantityConfirm: React.FC<QuantityConfirmProps> = ({
  pendingProduct,
  cartCode,
  onAccept,
  onReject,
  onCancel,
}) => {
  const { t } = useTranslation('merchandiseV3');
  const { toast } = useToast();
  const createBatchModalRef = useRef<ReceiveCreateBatchModalRef>(null);
  const [quantity, setQuantity] = useState(Math.min(pendingProduct.maxAvailableQty, 1));
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);

  const httpClient = createHttpClient(apiClientWH);
  const productAdapter = new ProductDimensionsApiAdapter(httpClient);

  const { data: productDetails, isLoading: isLoadingProduct } = useQuery({
    queryKey: ['receive-product-batch-active', pendingProduct.productId],
    queryFn: () => productAdapter.getById(pendingProduct.productId),
    staleTime: 60 * 1000,
  });

  const batchActive = Boolean(productDetails?.batch_active);
  const requiresBatch = batchActive;
  const canAccept = !isProcessing && !!cartCode && quantity > 0 && (!requiresBatch || !!selectedBatchId);

  const handleOpenCreateBatch = () => {
    createBatchModalRef.current?.open(
      {
        id: pendingProduct.productId,
        name: pendingProduct.productName,
        code: pendingProduct.productCode,
      },
      (batchId) => {
        setSelectedBatchId(batchId);
      },
    );
  };

  const handleAccept = async () => {
    if (!cartCode) {
      console.error('No hay código de carrito disponible');
      return;
    }

    if (requiresBatch && !selectedBatchId) {
      toast({
        title: t('receive.quantity_confirm.batch_required_title', 'Lote obligatorio'),
        description: t(
          'receive.quantity_confirm.batch_required_description',
          'Selecciona o registra un lote antes de aceptar el producto.',
        ),
        variant: 'destructive',
      });
      return;
    }

    setIsProcessing(true);
    try {
      await onAccept(pendingProduct.productId, cartCode, quantity, selectedBatchId ?? undefined);
    } catch (error) {
      console.error('Error al aceptar producto:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = () => {
    onReject(pendingProduct.productId, quantity);
  };

  const handleQuantityChange = (newQuantity: number) => {
    const safeQuantity = Number.isFinite(newQuantity) ? newQuantity : 1;
    setQuantity(Math.max(1, safeQuantity));
  };

  return (
    <>
      <div className="bg-background border border-border rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">
            {t('receive.quantity_confirm.title', 'Confirmar Cantidad')}
          </h3>
        </div>

        <div className="mb-4">
          <Product
            id={pendingProduct.productId}
            src=""
            name={pendingProduct.productName}
            code={pendingProduct.productCode}
            showImageModal={true}
            enableLineClamp={true}
          />
        </div>

        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">
              {t('receive.quantity_confirm.quantity_to_set', 'Cantidad a establecer')}
            </span>
            <span className="text-sm text-muted-foreground">
              {t('receive.quantity_confirm.pending', 'Pendiente')}: {pendingProduct.maxAvailableQty}
            </span>
          </div>

          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={() => handleQuantityChange(quantity - 1)}
              disabled={quantity <= 1}
              className="w-8 h-8 rounded border border-border bg-background hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-bold transition-colors"
            >
              −
            </button>

            <div className="flex-1">
              <input
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
                className="w-full px-3 py-2 text-center text-lg font-semibold border border-border rounded focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
              />
            </div>

            <button
              type="button"
              onClick={() => handleQuantityChange(quantity + 1)}
              className="w-8 h-8 rounded border border-border bg-background hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-bold transition-colors"
            >
              +
            </button>

            <button
              type="button"
              onClick={() => handleQuantityChange(pendingProduct.maxAvailableQty)}
              disabled={pendingProduct.maxAvailableQty <= 0}
              className="px-2 py-1 text-xs font-medium text-primary hover:text-primary/80 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('receive.quantity_confirm.max', 'Máx')}
            </button>
          </div>
        </div>

        {batchActive && (
          <div className="mb-4">
            <BatchSelect
              productId={pendingProduct.productId}
              value={selectedBatchId}
              onChange={setSelectedBatchId}
              disabled={isProcessing || isLoadingProduct}
              alwaysVisible
              onCreateClick={handleOpenCreateBatch}
              createLabel={t('receive.quantity_confirm.create_batch', 'Registrar lote')}
              emptyLabel={t(
                'receive.quantity_confirm.no_batches',
                'Este producto no tiene lotes. Registra uno para continuar con la recepción.',
              )}
            />
          </div>
        )}

        {cartCode && (
          <div className="mb-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                {t('receive.quantity_confirm.cart_location', 'Ubicación (Carrito)')}:
              </span>
              <span className="text-sm font-mono font-semibold text-blue-900 dark:text-blue-100">{cartCode}</span>
            </div>
          </div>
        )}

        <div className="flex space-x-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isProcessing}
            className="flex-1 py-2 px-3 border border-border rounded font-medium text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
          >
            <FontAwesomeIcon icon={faTimes} className="w-4 h-4 mr-2" />
            {t('receive.quantity_confirm.cancel', 'Cancelar')}
          </button>

          <button
            type="button"
            onClick={handleReject}
            disabled={isProcessing || quantity <= 0}
            className="flex-1 py-2 px-3 bg-red-600 hover:bg-red-700 text-white rounded font-medium focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
          >
            {t('receive.actions.reject', 'Rechazar')}
          </button>

          <button
            type="button"
            onClick={handleAccept}
            disabled={!canAccept}
            className="flex-1 py-2 px-3 bg-green-600 hover:bg-green-700 text-white rounded font-medium focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
          >
            {isProcessing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {t('receive.accept.processing', 'Procesando...')}
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faCheck} className="w-4 h-4 mr-2" />
                {t('receive.actions.accept', 'Aceptar')}
              </>
            )}
          </button>
        </div>
      </div>

      <ReceiveCreateBatchModal ref={createBatchModalRef} />
    </>
  );
};
